import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { getAssetProvenance, listAssets, listProjectAssets } from "./api";
import type { AssetListResponse, AssetProvenance, ListAssetsParams } from "./types";

export const assetKeys = {
    all: ["v2", "assets"] as const,
    list: (params: ListAssetsParams) => ["v2", "assets", "list", params] as const,
    projectList: (projectId: string, params: ListAssetsParams) =>
        ["v2", "assets", "project", projectId, "list", params] as const,
    provenance: (assetId: string) => ["v2", "assets", assetId, "provenance"] as const,
};

/**
 * Серверно-пагинированный листинг ассетов workspace. `keepPreviousData` сохраняет
 * прошлую страницу видимой во время загрузки следующей (как в чатах/проектах).
 */
export function useWorkspaceAssets(params: ListAssetsParams, options?: { enabled?: boolean }) {
    return useQuery<AssetListResponse>({
        queryKey: assetKeys.list(params),
        queryFn: () => listAssets(params),
        enabled: options?.enabled ?? true,
        placeholderData: keepPreviousData,
        staleTime: 30_000,
    });
}

/**
 * Серверно-пагинированный листинг ассетов одного проекта (scope = project). Зеркало
 * `useWorkspaceAssets`, но `projectId` уходит в путь; `enabled` гасит запрос без проекта.
 */
export function useProjectAssets(
    projectId: string,
    params: ListAssetsParams,
    options?: { enabled?: boolean },
) {
    return useQuery<AssetListResponse>({
        queryKey: assetKeys.projectList(projectId, params),
        queryFn: () => listProjectAssets(projectId, params),
        enabled: (options?.enabled ?? true) && !!projectId,
        placeholderData: keepPreviousData,
        staleTime: 30_000,
    });
}

export function useAssetProvenance(assetId: string, workspaceId?: string, options?: { enabled?: boolean }) {
    return useQuery<AssetProvenance>({
        queryKey: assetKeys.provenance(assetId),
        queryFn: () => getAssetProvenance(assetId, workspaceId),
        enabled: (options?.enabled ?? true) && !!assetId,
        staleTime: 60_000,
    });
}
